import React, { useEffect, useState } from "react";
import { useI18n } from "../context/I18nContext";

const SECTIONS = [
  { id: "avertissement", n: "1", t: "Avertissement général" },
  { id: "responsable", n: "2", t: "Responsable du traitement et contact" },
  { id: "categories", n: "3", t: "Catégories de données collectées" },
  { id: "finalites", n: "4", t: "Finalités du traitement et bases légales" },
  { id: "destinataires", n: "5", t: "Destinataires des données" },
  { id: "transferts", n: "6", t: "Transferts hors Union européenne" },
  { id: "duree", n: "7", t: "Durées de conservation" },
  { id: "securite", n: "8", t: "Mesures de sécurité" },
  { id: "droits", n: "9", t: "Droits des personnes concernées" },
  { id: "cookies", n: "10", t: "Gestion des cookies" },
  { id: "google", n: "11", t: "Connexion par Google Sign-In" },
  { id: "maj", n: "12", t: "Mise à jour de la politique" },
];

export default function Privacy() {
  const { t, lang } = useI18n();
  const [active, setActive] = useState(SECTIONS[0].id);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: "-30% 0px -60% 0px", threshold: 0.01 },
    );
    SECTIONS.forEach((s) => {
      const el = document.getElementById(s.id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  return (
    <div>
      <section className="border-b border-slate-200 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 sm:py-20">
          <div className="label-divider mb-6">{lang === "fr" ? "Confidentialité" : "Privacy"} · RGPD</div>
          <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900" data-testid="privacy-title">
            Politique de confidentialité
          </h1>
          <p className="mt-4 text-sm text-slate-500 font-mono">En vigueur au : 6 mai 2026</p>
          {lang === "en" && (
            <p className="mt-4 text-xs text-slate-500 max-w-2xl">
              The following privacy policy is intentionally provided in its authoritative French version (GDPR-aligned).
            </p>
          )}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-16">
        <div className="grid lg:grid-cols-12 gap-10">
          {/* TOC */}
          <aside className="lg:col-span-3">
            <div className="lg:sticky lg:top-24" data-testid="privacy-toc">
              <div className="text-[11px] uppercase tracking-[0.2em] text-slate-500 font-bold mb-4">Sommaire</div>
              <ol className="space-y-1.5">
                {SECTIONS.map((s) => (
                  <li key={s.id}>
                    <a
                      href={`#${s.id}`}
                      className={`block text-sm leading-snug py-1.5 pl-3 border-l-2 transition-colors ${
                        active === s.id
                          ? "border-slate-900 text-slate-900 font-semibold"
                          : "border-slate-200 text-slate-500 hover:text-slate-900 hover:border-slate-400"
                      }`}
                      data-testid={`privacy-toc-${s.id}`}
                    >
                      <span className="font-mono text-[11px] mr-2">{s.n}.</span>{s.t}
                    </a>
                  </li>
                ))}
              </ol>
            </div>
          </aside>

          {/* Content */}
          <article className="lg:col-span-9 space-y-14">
            <Section id="avertissement" n="1" title="Avertissement général">
              <p>
                La Société <strong className="text-slate-900">BUSINESS GPU LTD</strong> (ci-après la <em>« Société »</em>),
                éditrice du site <span className="font-mono text-slate-700">invest-gpu.com</span>, attache une importance
                fondamentale à la protection des données personnelles des utilisateurs du Site et des investisseurs (ci-après
                collectivement les <em>« Utilisateurs »</em>).
              </p>
              <p>
                La présente politique de confidentialité (ci-après la <em>« Politique »</em>) décrit les modalités de
                collecte, de traitement, de conservation et de protection des données personnelles des Utilisateurs,
                conformément au <strong className="text-slate-900">Règlement européen 2016/679 du 27 avril 2016</strong> relatif
                à la protection des personnes physiques à l'égard du traitement des données personnelles et à la libre
                circulation de ces données (ci-après le <em>« RGPD »</em>).
              </p>
              <p>
                Elle précise également les modalités de traitement des données relevant des obligations de lutte contre le
                blanchiment des capitaux et le financement du terrorisme (LCB-FT).
              </p>
              <p>
                En utilisant le Site, l'Utilisateur reconnaît avoir pris connaissance de la présente Politique et l'accepter
                sans réserve.
              </p>
            </Section>

            <Section id="responsable" n="2" title="Responsable du traitement et donnée de contact">
              <p>
                Le responsable du traitement des données personnelles collectées sur le Site est la Société{" "}
                <strong className="text-slate-900">BUSINESS GPU LTD</strong>, immatriculée sous le numéro{" "}
                <span className="font-mono text-slate-700">16264145</span>, dont le siège social est situé{" "}
                <strong className="text-slate-900">2 Flinders Drive, Peterborough, Cambridgeshire, United Kingdom, PE7 0NB</strong>.
              </p>
              <KV
                rows={[
                  ["Contact responsable du traitement", <a href="mailto:contact@invest-gpu.com" className="link-underline" key="r">contact@invest-gpu.com</a>],
                  ["Délégué à la Protection des Données (DPO)", <a href="mailto:dpo@invest-gpu.com" className="link-underline" key="d">dpo@invest-gpu.com</a>],
                  ["Service conformité (RGPD)", <a href="mailto:legal@invest-gpu.com" className="link-underline" key="l">legal@invest-gpu.com</a>],
                ]}
              />
            </Section>

            <Section id="categories" n="3" title="Catégories de données collectées">
              <SubH>3.1 Données d'identification et de contact</SubH>
              <p>Dans le cadre de la navigation sur le Site et de la création d'un compte, les données suivantes peuvent être collectées :</p>
              <BulletList items={[
                "Nom et prénom ;",
                "Adresse email ;",
                "Numéro de téléphone ;",
                "Adresse postale ;",
                "Date de naissance ;",
                "Nationalité ;",
                "Pays de résidence fiscale ;",
                "Langue de communication préférée.",
              ]} />
              <BasisNote>
                Exécution des conditions générales d'utilisation (art. <span className="font-mono">6.1.b</span> RGPD) et
                consentement de l'utilisateur (art. <span className="font-mono">6.1.a</span> RGPD).
              </BasisNote>

              <SubH>3.2 Données de connexion et techniques</SubH>
              <p>Les données suivantes sont collectées automatiquement lors de la consultation du Site :</p>
              <BulletList items={[
                "Adresse IP ;",
                "Type de navigateur et version ;",
                "Système d'exploitation ;",
                "Pages consultées et durée de consultation ;",
                "Logs de connexion (horodatage, session, action) ;",
                "Appareil utilisé (ordinateur, mobile, tablette) ;",
                "Cookies et traceurs (voir section 10).",
              ]} />
              <BasisNote>
                Intérêts légitimes de la Société à assurer la sécurité du Site, à prévenir les fraudes et à améliorer
                l'expérience utilisateur (art. <span className="font-mono">6.1.f</span> RGPD).
              </BasisNote>

              <SubH>3.3 Données de qualification investisseur (KYC)</SubH>
              <p>Dans le cadre du processus de qualification préalable à tout investissement, les données suivantes peuvent être collectées :</p>
              <BulletList items={[
                "Pièce d'identité (copie recto-verso) ;",
                "Justificatif de domicile (de moins de 3 mois) ;",
                "Justificatif de revenus et de patrimoine ;",
                "Attestation fiscale récente ;",
                "RIB ou IBAN ;",
                "Profil financier et expérience en investissement ;",
                "Réponses au questionnaire de connaissances et de compréhension des risques.",
              ]} />
              <BasisNote>
                Obligation légale (art. <span className="font-mono">6.1.c</span> RGPD) dans le cadre des obligations LCB-FT,
                et exécution des conditions générales d'investissement (art. <span className="font-mono">6.1.b</span> RGPD).
              </BasisNote>

              <SubH>3.4 Données bancaires et de paiement</SubH>
              <p>Les données suivantes sont collectées lors de la souscription et du paiement :</p>
              <BulletList items={[
                "Informations de carte bancaire (traitées exclusivement par Stripe) ;",
                "Montants investis ;",
                "Historique des transactions ;",
                "Références de paiement.",
              ]} />
              <p className="text-sm text-slate-700 bg-slate-50 border border-slate-200 px-4 py-3 rounded-sm">
                <strong className="text-slate-900">Important :</strong> les données bancaires ne sont jamais stockées sur les
                serveurs de la Société. Elles sont transmises directement au prestataire de paiement agréé{" "}
                <strong>Stripe</strong>, conforme à la norme <span className="font-mono">PCI-DSS niveau 1</span>.
              </p>
              <BasisNote>
                Exécution du contrat d'investissement (art. <span className="font-mono">6.1.b</span> RGPD).
              </BasisNote>

              <SubH>3.5 Données de communication</SubH>
              <p>Les données suivantes peuvent être collectées lors des échanges avec le service client :</p>
              <BulletList items={[
                "Contenu des emails, messages ou tickets de support ;",
                "Recommandations et notes internes liées aux interactions ;",
                "Enregistrements audio (uniquement avec consentement préalable et explicite).",
              ]} />
              <BasisNote>
                Intérêts légitimes du responsable de traitement à assurer le service client et à améliorer la qualité de
                service (art. <span className="font-mono">6.1.f</span> RGPD).
              </BasisNote>
            </Section>

            <Section id="finalites" n="4" title="Finalités du traitement et bases légales">
              <p>Les données personnelles collectées sont traitées pour les finalités suivantes :</p>

              <PurposeGrid items={[
                {
                  k: "4.1", t: "Gestion des comptes utilisateurs",
                  bullets: [
                    "Création et administration des comptes ;",
                    "Authentification et accès à l'espace investisseur ;",
                    "Communication d'informations relatives aux investissements ;",
                    "Support client.",
                  ],
                  basis: "Exécution du contrat (art. 6.1.b RGPD).",
                },
                {
                  k: "4.2", t: "Gestion des investissements",
                  bullets: [
                    "Gestion des souscriptions aux SPV ;",
                    "Distribution des revenus et dividendes ;",
                    "Reporting financier et transmission de documents ;",
                    "Communication sur l'évolution du portefeuille.",
                  ],
                  basis: "Exécution du contrat (art. 6.1.b RGPD).",
                },
                {
                  k: "4.3", t: "Vérification d'identité et conformité LCB-FT",
                  bullets: [
                    "Vérification d'identité (KYC) ;",
                    "Vérification de l'origine des fonds ;",
                    "Vérification des listes de sanctions (PEP, sanctions internationales) ;",
                    "Surveillance des transactions.",
                  ],
                  basis: "Obligation légale (art. 6.1.c RGPD) et intérêt légitime (art. 6.1.f RGPD).",
                },
                {
                  k: "4.4", t: "Paiements",
                  bullets: [
                    "Traitement des paiements des investissements ;",
                    "Remboursements le cas échéant.",
                  ],
                  basis: "Exécution du contrat (art. 6.1.b RGPD).",
                },
                {
                  k: "4.5", t: "Sécurité et prévention de la fraude",
                  bullets: [
                    "Détection et prévention des fraudes ;",
                    "Protection des systèmes et des données ;",
                    "Journalisation des accès.",
                  ],
                  basis: "Intérêt légitime (art. 6.1.f RGPD) et obligation légale (art. 6.1.c RGPD).",
                },
                {
                  k: "4.6", t: "Amélioration du service",
                  bullets: [
                    "Analyse statistique des usages (données anonymisées) ;",
                    "Amélioration de l'expérience utilisateur ;",
                    "Développement de nouvelles fonctionnalités.",
                  ],
                  basis: "Intérêt légitime (art. 6.1.f RGPD).",
                },
                {
                  k: "4.7", t: "Marketing et communication (consentement uniquement)",
                  bullets: [
                    "Envoi de newsletters ;",
                    "Informations sur les nouvelles opportunités d'investissement ;",
                    "Sondages de satisfaction.",
                  ],
                  basis: "Consentement (art. 6.1.a RGPD). Le consentement est révocable à tout moment via l'espace investisseur ou en contactant le service conformité.",
                },
              ]} />
            </Section>

            <Section id="destinataires" n="5" title="Destinataires des données">
              <SubH>5.1 En interne</SubH>
              <p>Les données personnelles collectées sont accessibles :</p>
              <BulletList items={[
                "Aux équipes dirigeantes et administratives de la Société ;",
                "Aux équipes techniques chargées de la maintenance du Site et des serveurs ;",
                "Aux équipes de conformité et juridique chargées des vérifications KYC/LCB-FT ;",
                "Aux équipes de service client.",
              ]} />
              <p>L'accès est strictement limité aux personnes habilitées au regard de leurs fonctions et soumis à une obligation de confidentialité.</p>

              <SubH>5.2 Sous-traitants et prestataires externes</SubH>
              <p>La Société fait appel aux prestataires suivants pour le traitement des données :</p>
              <ProviderTable rows={[
                ["Stripe", "Traitement des paiements par carte bancaire", "États-Unis (entité européenne en Irlande)", "https://stripe.com/fr/privacy"],
                ["Google Cloud / OAuth", "Authentification via Google Sign-In", "États-Unis", "https://policies.google.com/privacy"],
                ["Hostinger", "Hébergement du Site", "États-Unis", null],
                ["Resend", "Fournisseur d'email / CRM", "États-Unis", "https://resend.com/legal/privacy-policy"],
              ]} />
              <p className="text-sm">
                Certains de ces prestataires sont susceptibles de stocker des données en dehors de l'Union européenne,
                notamment aux États-Unis. Dans ce cas, la Société veille à ce que ces transferts soient encadrés par des{" "}
                <strong className="text-slate-900">Clauses Contractuelles Types (CCT)</strong> adoptées par la Commission
                européenne ou par d'autres garanties appropriées.
              </p>

              <SubH>5.3 Destinataires réglementaires</SubH>
              <p>Les données suivantes peuvent être transmises aux autorités compétentes dans le cadre des obligations légales :</p>
              <BulletList items={[
                "Autorités fiscales (DGFiP) ;",
                "Autorités de lutte contre le blanchiment (TRACFIN en France) ;",
                "Autorités judiciaires sur réquisition ;",
                "Autorités de régulation (AMF, ACPR) le cas échéant.",
              ]} />

              <SubH>5.4 SPV satellite</SubH>
              <p>
                Dans le cadre de l'investissement, les données d'identité et de contact de l'investisseur sont transmises aux
                SPV dans lesquels il investit, afin de permettre la tenue des registres d'associés. Cette transmission est
                strictement encadrée par les statuts du SPV et les contrats de souscription.
              </p>
            </Section>

            <Section id="transferts" n="6" title="Transferts de données hors de l'Union européenne">
              <p>Certaines données collectées peuvent être transférées vers des prestataires situés en dehors de l'Union européenne, notamment aux États-Unis d'Amérique. Les transferts suivants sont susceptibles d'avoir lieu :</p>
              <BulletList items={[
                <span key="s">Données de paiement : transmission à <strong className="text-slate-900">Stripe, Inc.</strong> (siège : États-Unis) ;</span>,
                <span key="g">Authentification Google : transmission à <strong className="text-slate-900">Google LLC</strong> (siège : États-Unis) ;</span>,
                <span key="h">Hébergement technique : transmission à <strong className="text-slate-900">Hostinger</strong> (siège : États-Unis).</span>,
              ]} />
              <p>
                Ces transferts sont encadrés par les <strong className="text-slate-900">Clauses Contractuelles Types (CCT)</strong>{" "}
                adoptées par la Commission européenne le <span className="font-mono">4 juin 2021</span>, ou par d'autres
                mécanismes de transfert garantissant un niveau de protection adéquat (adequacy decision, Privacy Shield si
                applicable).
              </p>
              <p>
                La Société peut fournir sur demande une copie des CCT en vigueur auprès de ses sous-traitants à l'adresse :{" "}
                <a href="mailto:legal@invest-gpu.com" className="link-underline text-slate-900 font-medium">legal@invest-gpu.com</a>.
              </p>
            </Section>

            <Section id="duree" n="7" title="Durées de conservation">
              <p>Les données personnelles sont conservées uniquement pendant la durée nécessaire aux finalités pour lesquelles elles ont été collectées, dans le respect des durées légales applicables :</p>
              <DataTable
                head={["Type de données", "Durée", "Fondement"]}
                rows={[
                  ["Données de compte utilisateur (contact, identification)", "3 ans après la dernière connexion", "Suppression ou anonymisation"],
                  ["Données de qualification investisseur (KYC, pièces d'identité)", "5 ans après la fin de la relation d'affaires", "Obligation légale LCB-FT"],
                  ["Données bancaires et de paiement", "5 ans après la transaction", "Obligation fiscale et comptable"],
                  ["Données de transaction et d'investissement", "10 ans", "Art. L. 123-24 Code de commerce"],
                  ["Données de connexion et logs techniques", "1 an", "Intérêt légitime de sécurité"],
                  ["Données de communication (support, emails)", "2 ans après la dernière interaction", "Intérêt légitime"],
                  ["Données marketing (avec consentement)", "Durée du consentement, révocable à tout moment", "Consentement"],
                  ["Données analytiques anonymisées", "Illimitée", "Données non personnelles"],
                ]}
              />
              <p>À l'issue des durées de conservation, les données sont soit supprimées de manière sécurisée, soit anonymisées de manière irréversible.</p>
            </Section>

            <Section id="securite" n="8" title="Mesures de sécurité">
              <p>La Société met en œuvre les mesures techniques et organisationnelles nécessaires pour garantir un niveau de sécurité adapté au risque de traitement des données personnelles :</p>
              <BulletList items={[
                <span key="1"><strong>Chiffrement</strong> des données en transit (<span className="font-mono">TLS 1.3</span>) et au repos (<span className="font-mono">AES-256</span>) ;</span>,
                <span key="2"><strong>Authentification forte (2FA)</strong> pour l'accès à l'espace investisseur ;</span>,
                "Ségrégation des environnements (production, développement, test) ;",
                "Journalisation et monitoring des accès aux données sensibles ;",
                "Procédures de sauvegarde sécurisées et plan de reprise d'activité ;",
                "Contrats de sous-traitance incluant des engagements de sécurité ;",
                "Sensibilisation des équipes internes aux obligations RGPD.",
              ]} />
              <p className="text-sm text-slate-700 bg-amber-50 border border-amber-200 px-4 py-3 rounded-sm">
                En cas de violation de données à caractère personnel susceptible d'engendrer un risque élevé pour les droits
                et libertés des personnes, la Société s'engage à <strong className="text-slate-900">notifier l'autorité de
                contrôle compétente (CNIL) dans un délai de 72 heures</strong> et à informer les personnes concernées si le
                risque est élevé.
              </p>
            </Section>

            <Section id="droits" n="9" title="Droits des personnes concernées">
              <p>Conformément au Règlement européen 2016/679 (RGPD), les Utilisateurs disposent des droits suivants sur leurs données personnelles :</p>
              <DataTable
                head={["Droit", "Description", "Comment l'exercer"]}
                rows={[
                  ["Droit d'accès", "Obtenir une copie de vos données personnelles", "Formulaire en ligne ou email à dpo@invest-gpu.com"],
                  ["Droit de rectification", "Corriger des données inexactes ou incomplètes", "Espace investisseur ou email à dpo@invest-gpu.com"],
                  ["Droit à l'effacement (oubli)", "Demander la suppression de vos données", "Email à dpo@invest-gpu.com (sous réserve LCB-FT)"],
                  ["Droit à la limitation", "Suspension temporaire du traitement", "Email à dpo@invest-gpu.com"],
                  ["Droit à la portabilité", "Données dans un format structuré", "Email à dpo@invest-gpu.com"],
                  ["Droit d'opposition", "Vous opposer au traitement", "Espace investisseur ou email à dpo@invest-gpu.com"],
                  ["Retrait du consentement", "Révoquer un consentement précédemment donné", "Espace investisseur ou email à dpo@invest-gpu.com"],
                  ["Directives post-mortem", "Définir le sort de vos données après votre décès", "Email à dpo@invest-gpu.com"],
                ]}
              />
              <Callout title="Délai de réponse">
                La Société s'engage à répondre à toute demande dans un délai d'<strong>un (1) mois</strong> à compter de la
                réception, conformément à l'article 12 du RGPD. Ce délai peut être prorogé de deux mois supplémentaires en
                cas de demande complexe ou nombreuse.
              </Callout>
              <Callout title="Limitation" tone="amber">
                Le droit à l'effacement ne peut être exercé pour les données dont la conservation est imposée par la loi,
                notamment les données liées aux obligations LCB-FT (5 ans minimum) et les données comptables et fiscales (10
                ans minimum).
              </Callout>
            </Section>

            <Section id="cookies" n="10" title="Gestion des cookies">
              <SubH>10.1 Qu'est-ce qu'un cookie ?</SubH>
              <p>
                Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, tablette, mobile) lors de votre
                visite du Site. Il permet à la Société de reconnaître votre terminal lors de vos visites suivantes et de
                personnaliser votre expérience.
              </p>

              <SubH>10.2 Types de cookies utilisés</SubH>
              <DataTable
                head={["Type de cookie", "Finalité", "Durée"]}
                rows={[
                  ["Strictement nécessaires", "Fonctionnement, authentification, panier de souscription", "Session à 1 an"],
                  ["Performance et analytiques", "Analyse statistique des audiences (Google Analytics)", "13 mois"],
                  ["Fonctionnels", "Préférences utilisateur (langue, thème)", "12 mois"],
                  ["Marketing", "Suivi advertising (uniquement avec consentement)", "13 mois"],
                ]}
              />

              <SubH>10.3 Gestion des cookies</SubH>
              <p>
                Lors de votre première visite, une bannière de consentement vous permet d'accepter ou de refuser les cookies
                non essentiels. Vos préférences sont enregistrées pour une durée de 13 mois.
              </p>
              <p>Vous pouvez modifier vos préférences à tout moment via le panneau de gestion des cookies accessible en bas de chaque page du Site.</p>
              <p>Vous pouvez également configurer directement votre navigateur pour bloquer ou supprimer les cookies :</p>
              <BulletList items={[
                "Chrome : Paramètres > Confidentialité et sécurité > Cookies",
                "Firefox : Options > Vie privée et sécurité > Cookies",
                "Safari : Préférences > Confidentialité > Bloquer les cookies",
              ]} />
            </Section>

            <Section id="google" n="11" title="Connexion par Google Sign-In">
              <SubH>11.1 Modalités</SubH>
              <p>InvestGPU propose la connexion par Google Sign-In (Google OAuth 2.0 / OpenID Connect) comme méthode alternative à la création de compte par email.</p>

              <SubH>11.2 Données collectées via Google Sign-In</SubH>
              <p>Lors de l'utilisation de Google Sign-In, les données suivantes sont récupérées depuis votre compte Google (avec votre consentement explicite) :</p>
              <BulletList items={[
                "Adresse email Google ;",
                "Nom et prénom ;",
                "Photo de profil (optionnelle) ;",
                "Identifiant Google unique.",
              ]} />
              <p className="text-sm text-slate-700 bg-emerald-50 border border-emerald-200 px-4 py-3 rounded-sm">
                <strong className="text-slate-900">Aucun mot de passe Google</strong> n'est collecté ou stocké par
                InvestGPU.
              </p>

              <SubH>11.3 Utilisation des données Google</SubH>
              <p>
                Les données récupérées via Google Sign-In sont utilisées aux mêmes finalités que les données collectées
                directement sur le Site (gestion du compte, communication, qualification investisseur).
              </p>

              <SubH>11.4 Révocation de l'accès</SubH>
              <p>
                Vous pouvez révoquer l'accès d'InvestGPU à votre compte Google à tout moment depuis :{" "}
                <a href="https://myaccount.google.com/permissions" target="_blank" rel="noopener noreferrer" className="link-underline text-slate-900 font-medium">
                  myaccount.google.com/permissions
                </a>.
              </p>
              <p>
                Pour toute question relative à la politique de confidentialité de Google, consultez :{" "}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer" className="link-underline text-slate-900 font-medium">
                  policies.google.com/privacy
                </a>.
              </p>
            </Section>

            <Section id="maj" n="12" title="Mise à jour de la politique">
              <p>
                La Société se réserve le droit de modifier la présente Politique de confidentialité à tout moment, notamment
                en cas d'évolution réglementaire, technologique ou organisationnelle.
              </p>
              <p>
                Les modifications seront portées à la connaissance des Utilisateurs par affichage sur le Site, avec mention
                de la date d'entrée en vigueur. En cas de modification substantielle impactant les droits des Utilisateurs,
                une notification par email sera envoyée aux investisseurs actifs.
              </p>
            </Section>

            <div className="border-t border-slate-200 pt-8 text-xs text-slate-500">
              <p className="font-mono">— Fin de la Politique de confidentialité —</p>
              <p className="mt-3 leading-relaxed">{t.footer.disclaimer}</p>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}

/* ---------- Helpers (mêmes conventions que Legal.jsx) ---------- */

function Section({ id, n, title, children }) {
  return (
    <section id={id} className="scroll-mt-24" data-testid={`privacy-section-${id}`}>
      <div className="flex items-baseline gap-4 mb-5">
        <span className="font-mono text-xs text-emerald-700 font-bold tracking-wider">{n}.</span>
        <h2 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">{title}</h2>
      </div>
      <div className="space-y-4 text-slate-700 leading-relaxed text-[15px] max-w-3xl">{children}</div>
    </section>
  );
}

function SubH({ children }) {
  return <h3 className="font-display text-base font-semibold text-slate-900 mt-6 mb-2 uppercase tracking-[0.05em]">{children}</h3>;
}

function BulletList({ items }) {
  return (
    <ul className="space-y-2 pl-1">
      {items.map((it, i) => (
        <li key={i} className="flex gap-3">
          <span className="text-emerald-700 mt-1.5 shrink-0 inline-block w-1 h-1 bg-emerald-700" />
          <span>{it}</span>
        </li>
      ))}
    </ul>
  );
}

function KV({ rows }) {
  return (
    <table className="w-full max-w-2xl mt-3 border border-slate-200 text-sm">
      <tbody>
        {rows.map(([k, v], i) => (
          <tr key={i} className={i > 0 ? "border-t border-slate-200" : ""}>
            <td className="py-2.5 px-4 text-slate-500 bg-slate-50 w-72">{k}</td>
            <td className="py-2.5 px-4 text-slate-900 font-medium">{v}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function BasisNote({ children }) {
  return (
    <p className="text-xs uppercase tracking-[0.1em] text-slate-500 mt-2">
      <span className="font-bold text-slate-700">Base légale :</span> {children}
    </p>
  );
}

function PurposeGrid({ items }) {
  return (
    <div className="grid sm:grid-cols-2 gap-4 mt-4 max-w-none">
      {items.map((p) => (
        <div key={p.k} className="border border-slate-200 bg-white p-5 rounded-sm">
          <div className="flex items-baseline gap-2 mb-2">
            <span className="font-mono text-[11px] text-emerald-700 font-bold">{p.k}</span>
            <h4 className="font-display font-semibold text-slate-900 text-base">{p.t}</h4>
          </div>
          <ul className="space-y-1.5 text-sm text-slate-600 mt-3">
            {p.bullets.map((b, i) => (
              <li key={i} className="flex gap-2">
                <span className="text-slate-400 shrink-0">—</span>
                <span>{b}</span>
              </li>
            ))}
          </ul>
          <div className="mt-4 pt-3 border-t border-slate-100 text-[11px] uppercase tracking-[0.1em] text-slate-500">
            <span className="font-bold text-slate-700">Base légale : </span>{p.basis}
          </div>
        </div>
      ))}
    </div>
  );
}

function ProviderTable({ rows }) {
  return (
    <div className="border border-slate-200 overflow-x-auto rounded-sm bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 bg-slate-50">
            <th className="py-3 px-4 font-bold">Prestataire</th>
            <th className="py-3 px-4 font-bold">Finalité</th>
            <th className="py-3 px-4 font-bold">Localisation</th>
            <th className="py-3 px-4 font-bold">Politique</th>
          </tr>
        </thead>
        <tbody>
          {rows.map(([name, desc, loc, url], i) => (
            <tr key={i} className="border-b border-slate-100 last:border-b-0">
              <td className="py-3 px-4 font-semibold text-slate-900">{name}</td>
              <td className="py-3 px-4 text-slate-700">{desc}</td>
              <td className="py-3 px-4 text-slate-700 font-mono text-xs">{loc}</td>
              <td className="py-3 px-4">
                {url ? (
                  <a href={url} target="_blank" rel="noopener noreferrer" className="link-underline text-slate-900 text-xs">Lien</a>
                ) : (
                  <span className="text-slate-400 text-xs">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DataTable({ head, rows }) {
  return (
    <div className="border border-slate-200 overflow-x-auto rounded-sm bg-white">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-[11px] uppercase tracking-[0.15em] text-slate-500 border-b border-slate-200 bg-slate-50">
            {head.map((h, i) => <th key={i} className="py-3 px-4 font-bold">{h}</th>)}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-slate-100 last:border-b-0">
              {r.map((c, j) => (
                <td key={j} className={`py-3 px-4 ${j === 0 ? "text-slate-900 font-medium" : "text-slate-700"}`}>{c}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Callout({ title, children, tone = "slate" }) {
  const cls = tone === "amber"
    ? "bg-amber-50 border-amber-200"
    : "bg-slate-50 border-slate-200";
  return (
    <div className={`border-l-2 ${tone === "amber" ? "border-amber-500" : "border-slate-900"} ${cls} px-4 py-3 rounded-sm text-sm text-slate-700 mt-4`}>
      <div className="text-[11px] uppercase tracking-[0.15em] font-bold text-slate-700 mb-1">{title}</div>
      <div>{children}</div>
    </div>
  );
}
